import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ArrowIcon} from '@/components/common/icons/ArrowIcon'
import {getClassAssistClassContent} from '@/content/classAssist'
import {Link, useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

type ClassAssistClassManualsProps = {
  classCode: string
  links: Record<string, string>
}

export const ClassAssistClassManuals: FC<React.PropsWithChildren<ClassAssistClassManualsProps>> = ({
  classCode,
  links,
}) => {
  const {t} = useTranslation()
  const {title, offices, visitWebsiteButtonText} = getClassAssistClassContent(classCode).manuals

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title, {classCode})}
      </Text>
      <ul className="list">
        {Object.entries(offices).map(([key, {logoSrc, logoAlt, manualTitle, dataKey}]) => (
          <li key={key} className="card">
            <Link href={links[dataKey]} legacyBehavior>
              <a
                target="_blank"
                className="link"
                onClick={() =>
                  analytics.track('Trademark class manual clicked', {manual: key, class: classCode})
                }
                role="link"
                tabIndex={0}
              >
                <div className="logo">
                  <Image
                    src={logoSrc}
                    alt={logoAlt}
                    style={{objectFit: 'contain'}}
                    fill
                    sizes={`${MOBILE_SCREENSIZE} 100vw, ${USER_TABLET_SCREENSIZE} 50vw, 25vw`}
                  />
                </div>
                <Text variant={TextVariant.h6} element="h3" fontWeight={TextFontWeight.bold}>
                  {t(manualTitle, {classCode})}
                </Text>
                <div className="button-wrapper">
                  <Button
                    type="button"
                    icon={<ArrowIcon size={IconSize.small} />}
                    size={ButtonSize.small}
                    isHoverable
                  >
                    {t(visitWebsiteButtonText)}
                  </Button>
                </div>
              </a>
            </Link>
          </li>
        ))}
      </ul>

      <style jsx>{`
        .list {
          display: flex;
          flex-wrap: wrap;
          margin: ${theme.spacing[3]} -${theme.spacing[3]} 0;
        }

        .card {
          width: 100%;
          max-width: calc(100% / 4 - ${theme.spacing[5]});
          background: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          border-radius: 12px;
          margin: ${theme.spacing[5]} ${theme.spacing[3]} 0;
          transition: box-shadow ${HOVER_TRANSITION}ms ease-in-out;
        }

        .card:hover {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
        }

        .link {
          display: block;
          padding: ${theme.spacing[4]};
        }

        .logo {
          position: relative;
          width: 100%;
          height: 120px;
          margin-bottom: ${theme.spacing[3]};
        }

        .button-wrapper {
          margin-top: ${theme.spacing[2]};
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .card {
            max-width: calc(100% / 2 - ${theme.spacing[5]});
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin: ${theme.spacing[5]} 0 0;
          }

          .card {
            margin: 0;
            max-width: inherit;
          }

          .card + .card {
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
